import { Button, Form, Modal, Table } from 'react-bootstrap';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';

import { AddFilled, ChevronLeft, InformationFilled } from '@carbon/icons-react';
import { Dataset } from './ReportModels';
import { useNavigate } from 'react-router-dom';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import ApiHelper from '../Shared/ApiHelper';
import { useMsal } from '@azure/msal-react';

interface DatasetSelectionModalProps {
    showModal: boolean;
    setShowModal: (show: boolean) => void;
}

const DatasetSelectionModal: FunctionComponent<DatasetSelectionModalProps> = (props: DatasetSelectionModalProps) => {
    const { showModal, setShowModal } = props;
    const [api] = useState(new ApiHelper());
    const { instance, accounts } = useMsal();
    const [datasetOptions, setDatasetOptions] = useState<Dataset[]>([]);
    const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
    const [showModelInfo, setShowModelInfo] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.callApi(
            instance,
            [process.env.REACT_APP_B2C_SCOPE ?? ''],
            process.env.REACT_APP_NET_API_URL + '/Dataset/private'
        ).then(async (result: Response) => {
            const data = await result.json();
            setDatasetOptions(data);
        }).catch((error) => {
            console.error('Failed to fetch datasets: ', error);
        });
    }, [api, instance]);

    return (
        <Modal
            scrollable
            show={showModal}
            onHide={() => {setShowModal(false); setSelectedDataset(null); setShowModelInfo(false);}}
            centered
            aria-labelledby="dataset-selection-modal"
            className="dataset-selection-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title id="dataset-selection-modal">Semantic Model Selection</Modal.Title>
            </Modal.Header>
            {!showModelInfo && (
                <>
                    <Modal.Body className="unset-overflow modal-info-slide-out">
                        <div  className="modal-info-container">
                            <span className="modal-info-icon">
                                <InformationFilled size={20} className="mr-2" />
                            </span>
                            <p>
                                For help selecting the right semantic model for the report you want to create, you may view a list of available options with descriptions <u className="modal-info-link" tabIndex={0} role="button" onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setShowModelInfo(true);
                                    }
                                }} onClick={() => setShowModelInfo(true)}>here.</u>
                            </p>
                        </div>
                        <Form className="p-3 dataset-selection-form">
                            <Form.Group controlId="DatasetSelect" className="mb-4">
                                <Form.Label>Please select a Semantic Model for your report</Form.Label>
                                {datasetOptions.length > 0 &&
                                    (<DropDownList
                                        data={datasetOptions}
                                        textField="name"
                                        dataItemKey="datasetId"
                                        value={selectedDataset}
                                        onChange={(e) => {
                                            const selected = e.value as Dataset;
                                            // Only set if it's a valid dataset with an ID (not the placeholder)
                                            setSelectedDataset(selected?.datasetId ? selected : null);
                                        }}
                                        defaultItem={{ name: 'Select a Semantic Model...', datasetId: '', isBaseline: false, application: '' }}
                                        className="dataset-multi-dropdown"
                                        popupSettings={{
                                            className: 'dataset-popup-custom'
                                        }}
                                        header={(
                                            <div className="dropdown-header">
                                                <div className="dropdown-header-col dropdown-header-col-name">Semantic Model Name</div>
                                                <div className="dropdown-header-col dropdown-header-col-type">Type</div>
                                                <div className="dropdown-header-col dropdown-header-col-app">Application</div>
                                            </div>
                                        )}
                                        itemRender={(li, itemProps) => {
                                            const dataItem = itemProps.dataItem as Dataset;
                                            return React.cloneElement(li, li.props, (
                                                <div className="dropdown-item-row">
                                                    <div className="dropdown-item-col dropdown-item-col-name">{dataItem.name}</div>
                                                    <div className="dropdown-item-col dropdown-item-col-type">{dataItem.isBaseline ? 'Baseline' : 'Custom'}</div>
                                                    <div className="dropdown-item-col dropdown-item-col-app">{dataItem.application}</div>
                                                </div>
                                            ));
                                        }}
                                        valueRender={(element, value) => {
                                            if (!value) return element;
                                            const dataItem = value as Dataset;
                                            return React.cloneElement(element, element.props, (
                                                <div className="dropdown-selected-value">
                                                    {dataItem.name}
                                                </div>
                                            ));
                                        }}
                                    />
                                    )}
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowModal(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={() => selectedDataset?.datasetId && navigate(`/reports/add/${selectedDataset.datasetId}`)}
                            disabled={!selectedDataset || !selectedDataset.datasetId}
                        >
                            <AddFilled size={24} className="button-icon-padding" data-testid="datasetAddReport" /> Add Report
                        </Button>
                    </Modal.Footer>
                </>

            )}
            {showModelInfo && (
                <>
                    <Modal.Body className="model-info-slide-in">
                        <button type="button" className="btn btn-link" onClick={() => setShowModelInfo(false)}>
                            <ChevronLeft size={16} />
                            Back to Form
                        </button>
                        <div className="semantic-model-list">
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {datasetOptions.map((dataset) => (
                                        <tr key={dataset.datasetId}>
                                            <td>{dataset.name}</td>
                                            <td>{dataset.description}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-primary"
                            onClick={() => {
                                setShowModelInfo(false);
                            }}
                        >
                            Back
                        </Button>
                    </Modal.Footer>
                </>
            )}

        </Modal>
    );
};

export default DatasetSelectionModal;
